import { useCallback, useMemo } from 'react';

import { YearlyVacationBalance } from '~/data/absences';
import {
  AbsenceStatus,
  AbsenceType,
  ShortAbsenceEntry,
} from '~/data/absences/types';
import { useEmployeeAbsenceData } from '~/data/absences/useEmployeeAbsenceData';
import { useApplicationConfig } from '~/data/applicationConfig';
import { EnumListKey, useAllEnums } from '~/data/enums';
import { useSelectedEmployeeId } from '~/hooks/employee';

export type YearlyBalanceTableRow = {
  year: number;
  reason: string;
  balance: number;
  proposed: number;
  approved: number;
  deducted: number;
  remaining: number;
};

export const useYearlyVacationBalanceTable = () => {
  const { applicationConfig } = useApplicationConfig();
  const isYearlyVacationBalanceEnabled =
    applicationConfig.fComConfig.absConfig.fComEnableYearlyVacationBalance;

  const { selectedEmployeeId } = useSelectedEmployeeId();
  const { absenceEntries, absencesData } = useEmployeeAbsenceData({
    employeeId: selectedEmployeeId,
  });

  const { getEnums } = useAllEnums();

  const { yearlyBalances, yearlyAbsences, reasons } = useMemo(() => {
    const yearlyBalances: YearlyVacationBalance[] =
      absencesData?.vacationBalance.fYearlyVacationBalances ?? [];

    const shortAbsenceEnums = getEnums(EnumListKey.ABSENCE_SHORT_REASON);

    const shortAbsences: ShortAbsenceEntry[] = absenceEntries.filter(
      (absence) =>
        absence.type === AbsenceType.SHORT &&
        absence.status !== AbsenceStatus.DRAFT,
    ) as ShortAbsenceEntry[];

    const yearlyAbsences = shortAbsences.filter((absence) => {
      return yearlyBalances.some(
        ({ fHolidayCode, fHolidayYear }) =>
          fHolidayYear === absence.year &&
          fHolidayCode === absence.reason?.extCode,
      );
    });

    return {
      yearlyBalances,
      reasons: {
        short: shortAbsenceEnums,
      },
      yearlyAbsences,
    };
  }, [absenceEntries, absencesData, getEnums]);

  const getYearlyBalanceCounts = useCallback(
    (yearlyBalance: YearlyVacationBalance) => {
      const { fHolidayBalance, fHolidayYear, fHolidayCode, fHolidayApproved } =
        yearlyBalance;

      const defaultStatusCounts = {
        balance: fHolidayBalance || 0,
        proposed: 0,
        approved: 0,
        deducted: 0,
        remaining: 0,
      };

      const counts = yearlyAbsences
        .filter((absence) => {
          return (
            fHolidayYear === absence.year &&
            fHolidayCode === absence.reason?.extCode
          );
        })
        .reduce((counts, absence) => {
          const isProposed = [
            AbsenceStatus.PROPOSED,
            AbsenceStatus.CHANGES_REQUESTED,
          ].includes(absence.status);
          const isApproved = absence.status === AbsenceStatus.APPROVED;

          const absenceDays = absence.days ?? 0;

          if (isProposed) {
            return {
              ...counts,
              proposed: counts.proposed + absenceDays,
            };
          } else if (isApproved) {
            return {
              ...counts,
              approved: counts.approved + absenceDays,
            };
          }

          return counts;
        }, defaultStatusCounts);

      return {
        ...counts,
        approved: fHolidayApproved,
        // BE doesn't yet provide info which absence has been deducted and which have not
        deducted: counts.approved - fHolidayApproved,
        remaining: fHolidayBalance - fHolidayApproved - counts.proposed,
      };
    },
    [yearlyAbsences],
  );

  const yearlyBalanceRows: YearlyBalanceTableRow[] = useMemo(() => {
    return yearlyBalances.map((yearlyBalance): YearlyBalanceTableRow => {
      const { fHolidayCode, fHolidayYear } = yearlyBalance;

      const absenceReason =
        reasons.short.find((e) => e.extCode === fHolidayCode)?.name ?? '';

      const counts = getYearlyBalanceCounts(yearlyBalance);

      return {
        year: fHolidayYear,
        reason: absenceReason,
        ...counts,
      };
    });
  }, [reasons, getYearlyBalanceCounts, yearlyBalances]);

  return {
    isEnabled: isYearlyVacationBalanceEnabled && yearlyBalanceRows.length > 0,
    yearlyBalanceRows,
  };
};
